<template>
    <c-modal :value="true" :title="`Update member`" @closed="cancelHandler">
        <validation-observer ref="createFormRef">
            <div class="form-group">
                <c-input v-model="memberModel.name" label="Name" label-class="text-muted small" class="mb-2" disabled />

                <c-input
                    v-model="memberModel.email"
                    label="Email"
                    label-class="text-muted small"
                    class="mb-2"
                    disabled
                />

                <validation-provider v-slot="{ failed }" rules="required">
                    <div class="mb-1">
                        <span class="text-muted small">Access level*</span>
                    </div>
                    <c-button-group
                        v-model="memberModel.accessType"
                        no-icon
                        type="radio"
                        :items="accessTypes"
                        :error="failed"
                        error-msg="Select the access mode"
                    />
                    <small class="mt-2 text-muted">
                        With restricted access, a member can only see allowed templates and the ones they create.
                    </small>
                    <c-custom-checkbox
                        v-if="memberModel.accessType == AccessMode.RESTRICTED"
                        v-model="memberModel.manageIntegrations"
                        label="Access Account Integrations"
                        class="mt-3"
                        switch
                    />
                </validation-provider>
            </div>
        </validation-observer>

        <template #modal-footer>
            <div class="d-flex justify-content-between w-100">
                <c-button text="Cancel" variant="secondary" @click="cancelHandler" />
                <c-button text="Update" variant="primary" @click="execLoadedFn(updateMember)" />
            </div>
        </template>
    </c-modal>
</template>

<script setup lang="ts">
import { Ref, onMounted, reactive, ref } from 'vue';
import { useLoader } from '@/composables/loader';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import CModal from '@/components/CModal.vue';
import CInput from '@/components/shared/CInput.vue';
import CButton from '@/components/shared/CButton';
import CCustomCheckbox from '@/components/shared/CCustomCheckbox.vue';
import CButtonGroup, { CButtonGroupItem } from '@/components/shared/CButtonGroup';
import { getTeamMemberPermissions, updateTeamMember } from '@/api/team';
import { AccessMode, EntityType, TeamMember } from '@/api/typings/team';

const emit = defineEmits<{
    (event: 'member-updated'): void;
    (event: 'handle-permissions', value: TeamMember): void;
    (event: 'cancel'): void;
}>();

const accessTypes: CButtonGroupItem[] = [
    {
        label: 'Full access',
        value: AccessMode.FULL
    },
    {
        label: 'Restricted',
        value: AccessMode.RESTRICTED
    }
];

const { execLoadedFn } = useLoader();

const createFormRef: Ref<InstanceType<typeof ValidationObserver> | null> = ref(null);

const props = defineProps<{
    member: TeamMember;
}>();

const memberModel = reactive({
    name: '',
    email: '',
    accessType: AccessMode.FULL,
    manageIntegrations: false
});

const updateMember = async (): Promise<void> => {
    await updateTeamMember(props.member.user.Id, memberModel.accessType, memberModel.manageIntegrations);

    emit('member-updated');
    if (memberModel.accessType == AccessMode.RESTRICTED) emit('handle-permissions', props.member);
};

const cancelHandler = () => emit('cancel');

onMounted(async () => {
    const permissions = await getTeamMemberPermissions(props.member.user.Id);
    memberModel.name = props.member.user.Name;
    memberModel.email = props.member.user.Email;
    memberModel.accessType = props.member.accessMode;
    memberModel.manageIntegrations = permissions.some(
        (p) => p.entityType == EntityType.INTEGRATIONS && p.accessLevel != 0
    );
});
</script>
